/**
 * Lift2Fit — coaching workspace: payments (`#/crm/payments`).
 *
 * A ledger, deliberately and only: who has paid, who is due, what has been
 * refunded and revenue by month. No invoicing, tax or gateway. Refusals come
 * from the API and are repeated word for word.
 */
import { useState } from 'react';
import { api, type CrmPayment, type CrmPaymentStatus, type Problem } from '@/lib/api';
import { formatCompactMoney } from '@/lib/format';
import { Button } from '@/components/ui';
import { AlertIcon, CheckIcon, TrashIcon, UndoIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  Field,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
  type ChipTone,
} from '../components';
import { useAsync } from '../hooks';
import {
  CRM_EYEBROW,
  CrmMessage,
  ListMore,
  QUEUE_LIST_LIMIT,
  RefusalNotice,
  formatDateShort,
  formatMoney,
  paymentStatusMeta,
  toDateInput,
} from './shared';

interface Notice {
  text: string;
  tone: ChipTone;
}

function currentMonthStart(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

export function CrmPaymentsScreen() {
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | CrmPaymentStatus>('');
  const [form, setForm] = useState(() => ({
    clientId: '',
    amount: '',
    currency: 'GBP',
    description: '',
    periodStart: currentMonthStart(),
    periodEnd: toDateInput(new Date()),
    dueOn: toDateInput(new Date()),
  }));
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The row whose delete is awaiting confirmation; null when none. */
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  /** The ledger starts bounded; the filters and this control reach the rest. */
  const [showAll, setShowAll] = useState(false);

  const paymentsState = useAsync(
    () => api.crmPayments({ clientId: clientFilter || null, status: statusFilter || null }),
    [clientFilter, statusFilter],
  );
  const revenueState = useAsync(() => api.crmRevenue(currentMonthStart(), toDateInput(new Date())), []);
  // 0030: the summary carries the money scope. An internal coach with no own
  // book gets `canSeeMoney: false` and the ledger is not rendered at all.
  const summaryState = useAsync(() => api.crmSummary(), []);
  const rosterState = useAsync(() => api.coachClients(), []);
  const clients = rosterState.data?.data ?? [];
  const payments = paymentsState.data?.data ?? [];
  const visiblePayments = showAll ? payments : payments.slice(0, QUEUE_LIST_LIMIT);
  const revenue = revenueState.data;

  const issue = (problem: Problem | undefined, fallbackText: string) => {
    setNotice(null);
    setRefusal(problem?.detail ?? problem?.title ?? fallbackText);
  };

  const succeed = (text: string) => {
    setRefusal(null);
    setNotice({ text, tone: 'success' });
    paymentsState.reload();
    revenueState.reload();
  };

  const create = async () => {
    setRefusal(null);
    setNotice(null);
    const amount = Number(form.amount);
    if (!form.clientId) {
      issue(undefined, 'Choose the client this payment is for.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      issue(undefined, 'Enter an amount greater than zero.');
      return;
    }
    setBusy('create');
    const result = await api.crmCreatePayment({
      clientId: form.clientId,
      amount,
      currency: form.currency.trim() === '' ? 'GBP' : form.currency.trim().toUpperCase(),
      description: form.description.trim() === '' ? null : form.description.trim(),
      periodStart: form.periodStart || null,
      periodEnd: form.periodEnd || null,
      dueOn: form.dueOn || null,
    });
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That payment was not created.');
      return;
    }
    setForm((previous) => ({ ...previous, amount: '', description: '' }));
    succeed(`Payment of ${formatMoney(result.data.amount, result.data.currency)} logged.`);
  };

  const setStatus = async (payment: CrmPayment, status: CrmPaymentStatus) => {
    setBusy(payment.paymentId);
    const result = await api.crmUpdatePayment(payment.paymentId, { status });
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That change was refused.');
      return;
    }
    succeed(`Payment marked ${status}.`);
  };

  /**
   * The mistake path: remove a row logged in error. Refunding is the normal
   * route for money that moved, so the button says so and asks first.
   */
  const remove = async (payment: CrmPayment) => {
    setBusy(payment.paymentId);
    const result = await api.crmDeletePayment(payment.paymentId);
    setBusy(null);
    setConfirmDelete(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That payment was not deleted.');
      return;
    }
    succeed('Payment deleted from the ledger.');
  };

  if (paymentsState.loading && !paymentsState.data) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading the ledger" rows={4} />
        <LoadingPanel label="Loading revenue" rows={3} />
      </div>
    );
  }

  // 0030: the restricted view comes BEFORE the "could not read" state, because
  // a refused ledger must read as "no access", not as a broken screen.
  if (summaryState.data && !summaryState.data.canSeeMoney) {
    return (
      <div className="flex flex-col gap-6" data-testid="crm-payments-restricted">
        <ScreenHeader
          eyebrow={CRM_EYEBROW}
          title="Payments"
          badge={<StatusChip tone="neutral">Restricted</StatusChip>}
          lead="Payments and revenue belong to the gym owner."
        />
        <EmptyState
          title="No access to payments"
          body="Internal coaches cannot see or change the gym's ledger, and the API refuses it too. The gym owner manages payments — ask them if you need a record."
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
          action={
            <AppLinkButton to="/crm" variant="outline">
              Back to the summary
            </AppLinkButton>
          }
        />
      </div>
    );
  }

  if (!paymentsState.data) {
    return (
      <EmptyState
        title="We could not read the ledger"
        body={paymentsState.error ?? 'The API did not answer. Check that you are signed in to a coaching workspace.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/crm" variant="outline">
            Back to the summary
          </AppLinkButton>
        }
      />
    );
  }

  const emptyTitle =
    statusFilter === 'due'
      ? 'No payments due'
      : statusFilter === 'paid'
        ? 'No payments paid yet'
        : statusFilter === 'refunded'
          ? 'No refunds recorded'
          : 'No payments yet';

  return (
    <div className="flex flex-col gap-6" data-testid="crm-payments">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Payments"
        badge={<StatusChip tone="accent">Ledger</StatusChip>}
        lead="Who has paid, who is due and what has been refunded. A ledger only — no invoicing, no tax, no payment gateway."
      />

      {refusal ? <RefusalNotice detail={refusal} className="mt-[-8px]" testId="crm-payments-refusal" /> : null}
      <CrmMessage message={notice?.text ?? null} tone={notice?.tone ?? 'info'} testId="crm-payments-notice" />

      <Panel
        testId="crm-revenue-summary"
        title="Revenue this month"
        subtitle={revenue ? `${formatDateShort(revenue.from)} – ${formatDateShort(revenue.to)} · ${revenue.currency}` : 'Paid, due and refunded by period.'}
      >
        {revenue ? (
          <>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <MetricTile
                label="Paid"
                value={formatCompactMoney(revenue.paid.total, revenue.currency)}
                valueTitle={formatMoney(revenue.paid.total, revenue.currency)}
                hint={`${revenue.paid.count} payment(s)`}
                hintTone="success"
                valueTestId="crm-revenue-tile-paid"
              />
              <MetricTile
                label="Due"
                value={formatCompactMoney(revenue.due.total, revenue.currency)}
                valueTitle={formatMoney(revenue.due.total, revenue.currency)}
                hint={`${revenue.due.count} outstanding`}
                hintTone={revenue.due.total > 0 ? 'warning' : 'success'}
                valueTestId="crm-revenue-tile-due"
              />
              <MetricTile
                label="Refunded"
                value={formatCompactMoney(revenue.refunded.total, revenue.currency)}
                valueTitle={formatMoney(revenue.refunded.total, revenue.currency)}
                hint={`${revenue.refunded.count} refund(s)`}
                valueTestId="crm-revenue-tile-refunded"
              />
            </dl>
            <div className="flex flex-wrap gap-6">
              <span data-testid="crm-revenue-paid" className="l2f-tabular font-mono text-[13px] text-ink-muted">
                paid {formatMoney(revenue.paid.total, revenue.currency)}
              </span>
              <span data-testid="crm-revenue-due" className="l2f-tabular font-mono text-[13px] text-ink-muted">
                due {formatMoney(revenue.due.total, revenue.currency)}
              </span>
              <span data-testid="crm-revenue-refunded" className="l2f-tabular font-mono text-[13px] text-ink-muted">
                refunded {formatMoney(revenue.refunded.total, revenue.currency)}
              </span>
            </div>
            {revenue.byPeriod.length ? (
              <ul className="flex flex-col divide-y divide-hairline">
                {revenue.byPeriod.map((period) => (
                  <li key={period.period} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">{period.period}</span>
                    <span className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-muted">
                      <span>paid {formatMoney(period.paid, revenue.currency)}</span>
                      <span>due {formatMoney(period.due, revenue.currency)}</span>
                      <span>refunded {formatMoney(period.refunded, revenue.currency)}</span>
                      <span>{period.count} row(s)</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-muted">No revenue in this period yet.</p>
            )}
          </>
        ) : (
          <p className="text-[13px] text-ink-muted">Revenue is unavailable right now.</p>
        )}
      </Panel>

      <Panel
        testId="crm-payment-form"
        title="New payment"
        subtitle="Log money owed. Marking it paid or refunded updates the revenue headline immediately."
      >
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Field label="Client">
            <select
              data-testid="crm-payment-client"
              value={form.clientId}
              onChange={(event) => setForm({ ...form, clientId: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            >
              <option value="">Choose a client…</option>
              {clients.map((client) => (
                <option key={client.userId} value={client.userId}>
                  {client.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Amount">
            <input
              type="number"
              min="0.01"
              step="0.01"
              data-testid="crm-payment-amount"
              value={form.amount}
              placeholder="0.00"
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </Field>
          <Field label="Currency">
            <input
              type="text"
              maxLength={3}
              data-testid="crm-payment-currency"
              value={form.currency}
              onChange={(event) => setForm({ ...form, currency: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] uppercase text-ink"
            />
          </Field>
          <Field label="Description">
            <input
              type="text"
              data-testid="crm-payment-description"
              value={form.description}
              placeholder="August coaching"
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </Field>
          <Field label="Period starts">
            <input
              type="date"
              data-testid="crm-payment-period-start"
              value={form.periodStart}
              onChange={(event) => setForm({ ...form, periodStart: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </Field>
          <Field label="Period ends">
            <input
              type="date"
              data-testid="crm-payment-period-end"
              value={form.periodEnd}
              onChange={(event) => setForm({ ...form, periodEnd: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </Field>
          <Field label="Due on" info="A due date in the past is what the summary counts as overdue.">
            <input
              type="date"
              data-testid="crm-payment-dueon"
              value={form.dueOn}
              onChange={(event) => setForm({ ...form, dueOn: event.target.value })}
              className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </Field>
          <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-3">
            <Button type="submit" data-testid="crm-payment-create" disabled={busy === 'create'}>
              {busy === 'create' ? 'Logging…' : 'Log due payment'}
            </Button>
            <Button
              variant="ghost"
              onClick={() =>
                setForm((previous) => ({ ...previous, amount: '', description: '' }))
              }
            >
              Clear
            </Button>
          </div>
        </form>
      </Panel>

      <Panel
        title="Ledger"
        subtitle="Filter by client and status. Every row is one payment."
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Client</span>
              <select
                data-testid="crm-payment-filter-client"
                value={clientFilter}
                onChange={(event) => setClientFilter(event.target.value)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="">All clients</option>
                {clients.map((client) => (
                  <option key={client.userId} value={client.userId}>
                    {client.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Status</span>
              <select
                data-testid="crm-payment-filter-status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as '' | CrmPaymentStatus)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="">All</option>
                <option value="due">Due</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            </label>
          </div>
        }
      >
        {payments.length ? (
          <ul className="flex flex-col divide-y divide-hairline">
            {visiblePayments.map((payment) => {
              const meta = paymentStatusMeta(payment.status);
              return (
                <li
                  key={payment.paymentId}
                  data-testid={`crm-payment-row-${payment.paymentId}`}
                  className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="l2f-tabular font-mono text-[15px] font-bold text-ink">
                        {formatMoney(payment.amount, payment.currency)}
                      </span>
                      <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
                      <span className="text-[13px] font-bold text-ink">{payment.clientName}</span>
                    </span>
                    <span className="text-[12px] text-ink-muted">
                      {payment.description ? `${payment.description} · ` : ''}
                      {payment.periodStart
                        ? `${formatDateShort(payment.periodStart)}${payment.periodEnd ? ` – ${formatDateShort(payment.periodEnd)}` : ''}`
                        : 'no period set'}
                      {payment.dueOn ? ` · due ${formatDateShort(payment.dueOn)}` : ''}
                      {payment.paidAt ? ` · paid ${formatDateShort(payment.paidAt)}` : ''}
                    </span>
                  </div>
                  <span className="flex flex-wrap items-center gap-2">
                    {payment.status === 'due' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`crm-payment-mark-paid-${payment.paymentId}`}
                        disabled={busy === payment.paymentId}
                        onClick={() => void setStatus(payment, 'paid')}
                      >
                        <CheckIcon />
                        Mark paid
                      </Button>
                    ) : null}
                    {payment.status !== 'refunded' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-payment-mark-refunded-${payment.paymentId}`}
                        disabled={busy === payment.paymentId}
                        onClick={() => void setStatus(payment, 'refunded')}
                      >
                        <UndoIcon />
                        Refund
                      </Button>
                    ) : null}
                    {payment.status === 'refunded' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-payment-mark-due-${payment.paymentId}`}
                        disabled={busy === payment.paymentId}
                        onClick={() => void setStatus(payment, 'due')}
                      >
                        Mark due
                      </Button>
                    ) : null}
                    {confirmDelete === payment.paymentId ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] text-ink-muted">Logged in error? A refund keeps the audit.</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`crm-payment-delete-confirm-${payment.paymentId}`}
                          disabled={busy === payment.paymentId}
                          onClick={() => void remove(payment)}
                        >
                          Confirm delete
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-payment-delete-${payment.paymentId}`}
                        disabled={busy === payment.paymentId}
                        onClick={() => setConfirmDelete(payment.paymentId)}
                      >
                        <TrashIcon />
                        Delete
                      </Button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div data-testid="crm-payments-empty">
            <EmptyState
              title={emptyTitle}
              body="Log a due payment above and it lands in this ledger. Marking it paid or refunded moves the revenue headline with it."
              icon={<AlertIcon className="h-8 w-8 text-accent" />}
            />
          </div>
        )}
        <ListMore
          total={payments.length}
          shown={visiblePayments.length}
          onShowAll={() => setShowAll(true)}
          toLabel="Show all"
          testId="crm-payments-more"
        />
      </Panel>
    </div>
  );
}
